import getUrl from '@utils/url-utils'

import {baseApi} from './base.slice'

const PLAYER_ENDPOINT = 'players'
const BASE_PLAYER_URL = new URL(
	`${process.env.GATSBY_BACKOFFICE_BASE_URL}/${PLAYER_ENDPOINT}`
).toString()

export const playerApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getPlayerProfiles: builder.query<
			GetPlayerProfilesResponse,
			{
				tenantId: string
				offset: number
				limit: number
				filter?: PropertyValuePair
				sort?: SortOptions
			}
		>({
			query: ({tenantId, offset, limit, sort, filter}) => {
				const url = getUrl(BASE_PLAYER_URL, {
					queryParams: new Map([
						['limit', limit.toString()],
						['offset', offset.toString()],
						['tenantId', tenantId],
					]),
				})

				if (sort) {
					url.searchParams.set('orderBy', `${sort.property}.${sort.direction}`)
				}

				const {property, value} = filter

				if (property && value) {
					const sanitizedValue = (
						property === 'email' ? value.toLowerCase() : value
					).trim()
					url.searchParams.set(property, sanitizedValue)
				}

				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
			providesTags: ['PlayerProfiles'],
		}),
		getPlayerProfile: builder.query<PlayerDetail, string>({
			query: (playerId) => {
				return {
					input: `${process.env.GATSBY_BACKOFFICE_BASE_URL}/${PLAYER_ENDPOINT}/${playerId}`,
					init: {method: 'GET'},
				}
			},
			providesTags: ['PlayerProfiles'],
		}),
		updatePlayerDetail: builder.mutation<
			PlayerDetail,
			{
				playerId: string
				isActive: boolean
				isFraudster: boolean
				isBlocked: boolean
				memberType: 'FREE' | 'MEMBER'
				shippingAddress: ShippingAddress
				isMarketingCommsEnabled: boolean
			}
		>({
			query: ({
				playerId,
				isFraudster,
				isActive,
				isBlocked,
				memberType,
				shippingAddress,
				isMarketingCommsEnabled,
			}) => {
				return {
					input: `${process.env.GATSBY_BACKOFFICE_BASE_URL}/${PLAYER_ENDPOINT}/${playerId}`,
					init: {
						method: 'PUT',
						body: JSON.stringify({
							// isActive,
							isFraudster,
							isBlocked,
							memberType,
							shippingAddress,
							isMarketingCommsEnabled,
						}),
					},
				}
			},
			invalidatesTags: ['PlayerProfiles'],
		}),

		deletePlayer: builder.mutation<
			null,
			{
				playerId: string
			}
		>({
			query: ({playerId}) => {
				return {
					input: `${process.env.GATSBY_BACKOFFICE_BASE_URL}/${PLAYER_ENDPOINT}/${playerId}`,
					init: {
						method: 'DELETE',
					},
				}
			},
			invalidatesTags: ['PlayerProfiles'],
		}),

		getGameSessions: builder.query<
			GetPlayerGameSessionsResponse,
			{
				playerId: string
				offset: number
				limit: number
				sort?: {direction: 'asc' | 'desc'; property: string}
			}
		>({
			query: ({playerId, offset, limit, sort}) => {
				const url = getUrl(BASE_PLAYER_URL, {
					appendPath: `${PLAYER_ENDPOINT}/${playerId}/game-sessions`,
					queryParams: new Map([
						['limit', limit.toString()],
						['offset', offset.toString()],
						['orderBy', sort ? `${sort.property}.${sort.direction}` : ''],
					]),
				})

				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
		}),
		getPlayerPrizes: builder.query<
			{entries: PlayerPrize[]; totalEntries: number},
			{
				playerId: string
				offset: number
				limit: number
			}
		>({
			query: ({playerId, offset, limit}) => {
				const url = getUrl(BASE_PLAYER_URL, {
					appendPath: `${PLAYER_ENDPOINT}/${playerId}/prizes`,
					queryParams: new Map([
						['orderBy', 'updatedAt.desc'],
						['limit', limit.toString()],
						['offset', offset.toString()],
					]),
				})

				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
			providesTags: ['PlayerPrizes'],
		}),
		addTicketsToPlayer: builder.mutation<
			null,
			{playerId: string; tickets: number}
		>({
			query: ({playerId, tickets}) => {
				return {
					input: `${BASE_PLAYER_URL}/${playerId}/tickets`,
					init: {method: 'PUT', body: JSON.stringify({tickets})},
				}
			},
			invalidatesTags: ['PlayerTransactions'],
		}),
		addTokensToPlayer: builder.mutation<
			null,
			{playerId: string; tokens: number}
		>({
			query: ({playerId, tokens}) => {
				return {
					input: `${BASE_PLAYER_URL}/${playerId}/tokens`,
					init: {method: 'PUT', body: JSON.stringify({tokens})},
				}
			},
			invalidatesTags: ['PlayerTransactions'],
		}),
		addPrizeToPlayer: builder.mutation<
			null,
			{playerId: string; prizeId: string}
		>({
			query: ({playerId, prizeId}) => {
				return {
					input: `${BASE_PLAYER_URL}/${playerId}/purchase`,
					init: {method: 'POST', body: JSON.stringify({prizeId})},
				}
			},
			invalidatesTags: ['PlayerTransactions'],
		}),
		getTransactions: builder.query<
			GetTransactionsResponse,
			{playerId: string; limit: number; offset: number}
		>({
			query: ({playerId, limit, offset}) => {
				const url = getUrl(`${BASE_PLAYER_URL}/${playerId}/transactions`, {
					queryParams: new Map([
						['limit', limit.toString()],
						['offset', offset.toString()],
					]),
				})
				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
			providesTags: ['PlayerTransactions'],
		}),
		getPlayerIncidents: builder.query<
			any,
			{playerId: string; limit: number; offset: number; sort?: SortOptions}
		>({
			query: ({playerId, limit, offset, sort}) => {
				const url = getUrl(`${BASE_PLAYER_URL}/${playerId}/incidents`, {
					queryParams: new Map([
						['limit', limit.toString()],
						['offset', offset.toString()],
						['orderBy', sort ? `${sort.property}.${sort.direction}` : ''],
					]),
				})
				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
		}),
	}),
})

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints.
 */
export const {
	useLazyGetPlayerProfileQuery,
	useGetPlayerProfileQuery,
	useLazyGetPlayerProfilesQuery,
	useGetPlayerProfilesQuery,
	useUpdatePlayerDetailMutation,
	useDeletePlayerMutation,
	useGetGameSessionsQuery,
	useLazyGetGameSessionsQuery,
	useLazyGetPlayerPrizesQuery,
	useAddPrizeToPlayerMutation,
	useAddTokensToPlayerMutation,
	useAddTicketsToPlayerMutation,
	useLazyGetTransactionsQuery,
	useGetPlayerIncidentsQuery,
	useLazyGetPlayerIncidentsQuery,
} = playerApi
export default playerApi.reducer
